/* iter-p3-w — Sparkline deep-dive dialog.
 *
 * On tap of the AI-share sparkline point, we fetch
 * `GET /editing-record/user-trends/{day}` and list the projects
 * that contributed to that day's AI-share.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogDescription } from '@/components/ui/dialog';
import { Loader2, Activity } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function AiShareTrendDeepDiveDialog({ open, onOpenChange, day }) {
  const [rows, setRows] = useState(null);
  // iter-p3-x — Per-project 30-day series for the second-tier chart.
  const [projSeries, setProjSeries] = useState({});
  const [projId,     setProjId]     = useState(null);

  useEffect(() => {
    if (!open || !day) { setRows(null); setProjId(null); return; }
    let cancelled = false;
    axios.get(`${API}/editing-record/user-trends/${day}`,
              { withCredentials: true, timeout: 15000 })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.projects || [];
        setRows(list);
        if (list.length > 0) setProjId(list[0].project_id);
      })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [open, day]);

  // Fetch a 30d per-project trend when a row is selected.  Cached by
  // project_id so re-selecting doesn't re-fire.
  useEffect(() => {
    if (!projId || projSeries[projId]) return;
    let cancelled = false;
    axios.get(`${API}/editing-record/user-trends?days=30`,
              { withCredentials: true, timeout: 15000, params: { project_id: projId } })
      .then((r) => {
        if (cancelled) return;
        setProjSeries((prev) => ({ ...prev, [projId]: r.data?.series || [] }));
      })
      .catch(() => { if (!cancelled) setProjSeries((prev) => ({ ...prev, [projId]: [] })); });
    return () => { cancelled = true; };
  }, [projId]); // eslint-disable-line

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ai-share-deepdive">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" /> {day} · project breakdown
          </DialogTitle>
          <DialogDescription>
            Every project you touched on {day}, sorted by action volume. Tap any row to open the project.
          </DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <div className="flex items-center gap-2 py-6 text-[12px] text-muted-foreground" data-testid="ai-share-deepdive-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-[12px] text-muted-foreground" data-testid="ai-share-deepdive-empty">
            No editing-record actions on this day.
          </div>
        ) : (
          <>
            <div className="max-h-64 overflow-auto rounded-md border border-border" data-testid="ai-share-deepdive-list">
              <table className="w-full text-left text-[12.5px]">
                <thead className="bg-muted/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Project</th>
                    <th className="px-3 py-2 text-right w-16">AI</th>
                    <th className="px-3 py-2 text-right w-20">Human</th>
                    <th className="px-3 py-2 text-right w-20">AI %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.project_id}
                        onClick={() => setProjId(r.project_id)}
                        className={
                          'cursor-pointer border-t border-border '
                          + (projId === r.project_id ? 'bg-primary/10' : 'hover:bg-muted/30')
                        }
                        data-testid={`ai-share-deepdive-row-${r.project_id}`}>
                      <td className="px-3 py-2">
                        <a className="text-primary hover:underline"
                           href={`/app?project=${r.project_id}`}
                           onClick={(e) => e.stopPropagation()}
                           data-testid={`ai-share-deepdive-open-${r.project_id}`}>
                          {r.project_title}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat">{r.ai_actions}</td>
                      <td className="px-3 py-2 text-right font-mono-stat">{r.human_actions}</td>
                      <td className={
                        'px-3 py-2 text-right font-mono-stat font-semibold '
                        + (r.ai_share_pct >= 66 ? 'text-amber-700'
                          : r.ai_share_pct >= 34 ? 'text-emerald-700' : 'text-sky-700')
                      }>
                        {r.ai_share_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {projId && (
              <div className="mt-4" data-testid="ai-share-deepdive-project-series">
                <div className="mb-1 flex items-center justify-between text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <span>30-day momentum · {rows.find((r) => r.project_id === projId)?.project_title || projId}</span>
                  <span>Height = actions · Colour tint = AI share</span>
                </div>
                <MiniBarChart series={projSeries[projId]} />
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Compact 30-bar chart. Bar height ∝ total actions that day, colour
 * shifts amber/emerald/sky by AI-share bucket. Rendered pure SVG so we
 * don't drag a charting library in for a 60px card.
 */
function MiniBarChart({ series }) {
  if (!series) return <div className="h-16 rounded-md border border-dashed border-border bg-muted/20" />;
  if (series.length === 0) {
    return <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-center text-[11px] text-muted-foreground">No actions on this project in the window.</div>;
  }
  const W = 300, H = 54, PAD = 3;
  const max = Math.max(1, ...series.map((s) => s.total || 0));
  const bw = (W - PAD * 2) / series.length - 1;
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}
         preserveAspectRatio="none"
         className="block"
         data-testid="ai-share-deepdive-barchart">
      {series.map((s, i) => {
        const h = ((s.total || 0) / max) * (H - PAD * 2);
        const x = PAD + i * ((W - PAD * 2) / series.length);
        const share = typeof s.ai_share_pct === 'number' ? s.ai_share_pct : 0;
        const fill = s.total === 0 ? '#e5e7eb'
          : share >= 66 ? '#f59e0b'
          : share >= 34 ? '#10b981' : '#0ea5e9';
        return (
          <g key={s.day}>
            <rect x={x} y={H - PAD - h} width={Math.max(1, bw)} height={h} fill={fill} rx="0.5">
              <title>{`${s.day} · ${s.total} action${s.total === 1 ? '' : 's'}${typeof s.ai_share_pct === 'number' ? ` · ${s.ai_share_pct}% AI` : ''}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}
